import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LuArrowLeft, LuChevronRight, LuFileQuestion } from "react-icons/lu";
import SeoHead from "../../components/SeoHead";
import parseFrontmatter from "../../utils/parseFrontmatter";
import remarkGfm from "remark-gfm";

export default function BlogPost() {
  const { slug } = useParams();
  const [meta, setMeta] = useState({});
  const [content, setContent] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setContent("");
    setMeta({});

    // Sanitize slug (Path Traversal Prevention)
    const sanitizedSlug = String(slug).replace(/[^a-z0-9-]/gi, '').substring(0, 100);

    if (!sanitizedSlug) {
      setNotFound(true);
      return;
    }

    let cancelled = false;
    fetch(`/blog/${sanitizedSlug}.md`)
      .then(res => {
        if (!res.ok) throw new Error('Artikel nicht gefunden');
        return res.text();
      })
      .then(md => {
        // Unbekannte Pfade liefert der Host als index.html (SPA-Fallback) aus – das ist kein Artikel
        if (/^\s*(<!doctype html|<html)/i.test(md)) throw new Error('Artikel nicht gefunden');
        const { data, content } = parseFrontmatter(md);
        if (cancelled) return;
        setMeta(data);
        setContent(content);
      })
      .catch(err => {
        console.error('Blog load error:', err);
        if (!cancelled) setNotFound(true);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) return (
    <section className="section">
      <Helmet>
        <meta name="robots" content="noindex, follow" />
        <title>Artikel nicht gefunden – edi-converter.de</title>
      </Helmet>
      <div className="container container-sm text-center">
        <span className="icon-tile icon-tile-lg"><LuFileQuestion size={24} aria-hidden="true" /></span>
        <h1 className="h2" style={{ marginTop: 16 }}>Artikel nicht gefunden</h1>
        <p className="lead" style={{ marginTop: 10 }}>
          Diesen Beitrag gibt es nicht (mehr). Vielleicht findest du im Blog, was du suchst.
        </p>
        <div className="row row-center" style={{ marginTop: 28 }}>
          <Link to="/blog" className="btn btn-primary"><LuArrowLeft size={16} aria-hidden="true" /> Zum Blog</Link>
          <Link to="/edi-konverter" className="btn btn-secondary">Zum EDI Konverter</Link>
        </div>
      </div>
    </section>
  );

  if (!content) return (
    <section className="section">
      <div className="container row row-center" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <span className="muted">Artikel wird geladen …</span>
      </div>
    </section>
  );

  return (
    <>
      <SeoHead
        title={meta.title || "Blog"}
        description={meta.description || meta.excerpt || "Artikel"}
        ogImage={meta.image ? `https://edi-converter.de${meta.image}` : "https://edi-converter.de/social.jpg"}
        ogUrl={`https://edi-converter.de/blog/${slug}`}
      />

      <article className="section-sm">
        <div className="container container-sm">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/blog">Blog</Link>
            <LuChevronRight size={14} aria-hidden="true" />
            <span aria-current="page" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60ch" }}>
              {meta.title || slug}
            </span>
          </nav>

          {meta.image && (
            <img
              src={meta.image}
              alt={meta.title}
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                borderRadius: "var(--radius-xl)",
                border: "1px solid var(--border)",
                background: "var(--bg-muted)",
                margin: "8px 0 40px",
              }}
            />
          )}

          <div className="prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </div>

        </div>
      </article>
    </>
  );
}
